import React, { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Plus, Edit, Trash2, ArrowLeft } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { useSchools } from '@/hooks/useSchools'

interface Reward {
  id: string
  account_type: string
  domain_type: string
  subheader: string
  description: string
  reward_code: string
  terms_conditions: string
  level_1_discount: string
  level_2_discount: string
  level_3_discount: string
  is_active: boolean
  school_id: string | null
  created_at: string
  schools?: {
    id: string
    name: string
  } | null
}

const AdminRewards = () => {
  const [rewards, setRewards] = useState<Reward[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editingReward, setEditingReward] = useState<Reward | null>(null)
  const [activeTab, setActiveTab] = useState('global')
  const { toast } = useToast()
  const { schools, loading: schoolsLoading } = useSchools()

  const [formData, setFormData] = useState({
    account_type: '',
    domain_type: '',
    subheader: '',
    description: '',
    terms_conditions: '',
    level_1_discount: '',
    level_2_discount: '',
    level_3_discount: '',
    school_id: 'global'
  })

  const domainTypes = [
    { value: 'self', label: 'Self' },
    { value: 'wealth', label: 'Wealth' },
    { value: 'social', label: 'Social' }
  ]

  useEffect(() => {
    fetchRewards()
  }, [])

  const fetchRewards = async () => {
    try {
      const { data, error } = await supabase
        .from('rewards')
        .select(`
          *,
          schools (
            id,
            name
          )
        `)
        .order('created_at', { ascending: false })

      if (error) throw error
      setRewards(data || [])
    } catch (error: any) {
      toast({
        title: "Error fetching rewards",
        description: error.message,
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  // Filter rewards based on active tab
  const filteredRewards = rewards.filter((reward) => {
    if (activeTab === 'global') {
      return !reward.school_id
    } else {
      return reward.school_id
    }
  })

  // Group school-specific rewards by school
  const rewardsBySchool = filteredRewards.reduce((acc: any, reward: Reward) => {
    if (reward.school_id) {
      const schoolName = reward.schools?.name || 'Unknown School'
      if (!acc[schoolName]) {
        acc[schoolName] = []
      }
      acc[schoolName].push(reward)
    }
    return acc
  }, {})

  const validateLevels = () => {
    // Level 1 is always required
    if (!formData.level_1_discount) {
      return "Level 1 discount is required"
    }
    
    // If Level 2 is provided, Level 1 must exist
    if (formData.level_2_discount && !formData.level_1_discount) {
      return "Level 1 must be provided before Level 2"
    }
    
    // If Level 3 is provided, both Level 1 and 2 must exist
    if (formData.level_3_discount && (!formData.level_1_discount || !formData.level_2_discount)) {
      return "Level 1 and 2 must be provided before Level 3"
    }
    
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate levels
    const levelError = validateLevels()
    if (levelError) {
      toast({
        title: "Level validation error",
        description: levelError,
        variant: "destructive",
      })
      return
    }

    try {
      const rewardData = {
        ...formData,
        school_id: formData.school_id === 'global' ? null : formData.school_id || null
      }

      if (editingReward) {
        const { error } = await supabase
          .from('rewards')
          .update(rewardData)
          .eq('id', editingReward.id)
        
        if (error) throw error
        toast({ title: "Reward updated successfully" })
      } else {
        const { error } = await supabase
          .from('rewards')
          .insert(rewardData)
        
        if (error) throw error
        toast({ title: "Reward created successfully" })
      }

      setDialogOpen(false)
      setEditingReward(null)
      setFormData({
        account_type: '',
        domain_type: '',
        subheader: '',
        description: '',
        terms_conditions: '',
        level_1_discount: '',
        level_2_discount: '',
        level_3_discount: '',
        school_id: 'global'
      })
      fetchRewards()
    } catch (error: any) {
      toast({
        title: "Error saving reward",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const handleEdit = (reward: Reward) => {
    setEditingReward(reward)
    setFormData({
      account_type: reward.account_type,
      domain_type: reward.domain_type,
      subheader: reward.subheader || '',
      description: reward.description || '',
      terms_conditions: reward.terms_conditions || '',
      level_1_discount: reward.level_1_discount,
      level_2_discount: reward.level_2_discount,
      level_3_discount: reward.level_3_discount,
      school_id: reward.school_id || 'global'
    })
    setDialogOpen(true)
  }

  const handleDelete = async (rewardId: string) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .delete()
        .eq('id', rewardId)

      if (error) throw error
      toast({ title: "Reward deleted successfully" })
      fetchRewards()
    } catch (error: any) {
      toast({
        title: "Error deleting reward",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  const toggleActiveStatus = async (reward: Reward) => {
    try {
      const { error } = await supabase
        .from('rewards')
        .update({ is_active: !reward.is_active })
        .eq('id', reward.id)

      if (error) throw error
      toast({ title: `Reward ${!reward.is_active ? 'activated' : 'deactivated'} successfully` })
      fetchRewards()
    } catch (error: any) {
      toast({
        title: "Error updating reward status",
        description: error.message,
        variant: "destructive",
      })
    }
  }

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Rewards Management</CardTitle>
          <CardDescription>Loading rewards...</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Rewards Management</h1>
            <p className="text-muted-foreground">Create and manage student rewards and discounts</p>
          </div>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => {
                setEditingReward(null)
                setFormData({
                  account_type: '',
                  domain_type: '',
                  subheader: '',
                  description: '',
                  terms_conditions: '',
                  level_1_discount: '',
                  level_2_discount: '',
                  level_3_discount: '',
                  school_id: 'global'
                })
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Reward
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingReward ? 'Edit Reward' : 'Add Reward'}</DialogTitle>
              <p className="text-sm text-muted-foreground">
                Here you can see all about your Reward.
              </p>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Reward Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="account_type">Type Of Account *</Label>
                  <Input
                    id="account_type"
                    value={formData.account_type}
                    onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
                    placeholder="e.g., Breathwrk App, Apple, Premium"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="domain_type">Domain Type *</Label>
                  <Select 
                    value={formData.domain_type} 
                    onValueChange={(value) => setFormData({ ...formData, domain_type: value })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select domain type" />
                    </SelectTrigger>
                    <SelectContent>
                      {domainTypes.map(domain => (
                        <SelectItem key={domain.value} value={domain.value}>
                          {domain.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Target University */}
              <div className="space-y-2">
                <Label htmlFor="school_id">Target University *</Label>
                <Select 
                  value={formData.school_id} 
                  onValueChange={(value) => setFormData({ ...formData, school_id: value })}
                  required
                  disabled={schoolsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select target university" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="global">Global (All Universities)</SelectItem>
                    {schools.map(school => (
                      <SelectItem key={school.id} value={school.id}>
                        {school.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Select "Global" for rewards available to all universities, or choose a specific university.
                </p>
              </div>

              {/* Subheader */}
              <div className="space-y-2">
                <Label htmlFor="subheader">Subheader *</Label>
                <Input
                  id="subheader"
                  value={formData.subheader}
                  onChange={(e) => setFormData({ ...formData, subheader: e.target.value })}
                  placeholder="e.g., 90 Days Free - Harness your breath, live with less stress"
                  required
                />
              </div>

              {/* Image Upload Placeholder */}
              <div className="space-y-2">
                <Label>Image</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <p className="text-muted-foreground">Image upload functionality will be added later</p>
                  <Button type="button" variant="outline" className="mt-2" disabled>
                    + Choose File
                  </Button>
                </div>
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Detailed description of the reward..."
                  className="min-h-[100px]"
                  required
                />
              </div>

              {/* Terms and Conditions */}
              <div className="space-y-4">
                <div>
                  <Label className="text-base font-medium">Terms And Conditions</Label>
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="terms_method"
                          value="write"
                          defaultChecked
                          className="rounded"
                        />
                        <span className="text-sm">Write Terms & Conditions</span>
                      </label>
                      <label className="flex items-center space-x-2">
                        <input
                          type="radio"
                          name="terms_method"
                          value="upload"
                          disabled
                          className="rounded"
                        />
                        <span className="text-sm text-muted-foreground">Upload Document</span>
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Terms & Conditions Content</Label>
                  <div className="border rounded-lg">
                    {/* Rich Text Editor Toolbar */}
                    <div className="border-b bg-gray-50 p-2 flex flex-wrap gap-1">
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">H1</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">H2</Button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">Sans Serif</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">Normal</Button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 font-bold">B</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 italic">I</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 underline">U</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2 line-through">S</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">"</Button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">•</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">1.</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">←</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">→</Button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">🔗</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">🖼️</Button>
                      <div className="w-px h-6 bg-gray-300 mx-1"></div>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">A</Button>
                      <Button type="button" variant="ghost" size="sm" className="h-8 px-2">Tx</Button>
                    </div>
                    <Textarea
                      value={formData.terms_conditions}
                      onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                      placeholder="Enter terms and conditions here..."
                      className="min-h-[200px] border-0 focus-visible:ring-0"
                    />
                  </div>
                </div>
              </div>

              {/* Level-based Discounts */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Level-based Discounts & Codes</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Level 1</Label>
                    <Input
                      value="1"
                      disabled
                      className="bg-gray-50"
                    />
                    <Input
                      value={formData.level_1_discount}
                      onChange={(e) => setFormData({ ...formData, level_1_discount: e.target.value })}
                      placeholder="e.g., 90 Days Free - DABLY90 *"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Level 2</Label>
                    <Input
                      value="2"
                      disabled
                      className="bg-gray-50"
                    />
                    <Input
                      value={formData.level_2_discount}
                      onChange={(e) => setFormData({ ...formData, level_2_discount: e.target.value })}
                      placeholder="e.g., 6 Months Free - DABLY180 *"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Level 3</Label>
                    <Input
                      value="3"
                      disabled
                      className="bg-gray-50"
                    />
                    <Input
                      value={formData.level_3_discount}
                      onChange={(e) => setFormData({ ...formData, level_3_discount: e.target.value })}
                      placeholder="e.g., 1 Year Free - DABLY365 *"
                      required
                    />
                  </div>
                </div>
              </div>

              <Button type="submit" className="w-full">
                {editingReward ? 'Update Reward' : 'Create Reward'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Rewards List */}
      <Card>
        <CardHeader>
          <CardTitle>Existing Rewards</CardTitle>
          <CardDescription>Manage all created rewards and their status</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList>
              <TabsTrigger value="global">Global Rewards</TabsTrigger>
              <TabsTrigger value="school-specific">School-Specific Rewards</TabsTrigger>
            </TabsList>
            
            <TabsContent value="global" className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Account Type</TableHead>
                    <TableHead>Subheader</TableHead>
                    <TableHead>Domain</TableHead>
                    <TableHead>Level 1</TableHead>
                    <TableHead>Level 2</TableHead>
                    <TableHead>Level 3</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRewards.length > 0 ? (
                    filteredRewards.map((reward) => (
                      <TableRow key={reward.id}>
                        <TableCell className="font-medium">{reward.account_type}</TableCell>
                        <TableCell className="max-w-[200px] truncate" title={reward.subheader}>
                          {reward.subheader || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{reward.domain_type}</Badge>
                        </TableCell>
                        <TableCell>{reward.level_1_discount}</TableCell>
                        <TableCell>{reward.level_2_discount || '-'}</TableCell>
                        <TableCell>{reward.level_3_discount || '-'}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={reward.is_active ? "default" : "secondary"}
                            className="cursor-pointer"
                            onClick={() => toggleActiveStatus(reward)}
                          >
                            {reward.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleEdit(reward)}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(reward.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No global rewards created yet. Click "Add Reward" to create your first reward.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TabsContent>
            
            <TabsContent value="school-specific" className="mt-4">
              {Object.keys(rewardsBySchool).length > 0 ? (
                Object.entries(rewardsBySchool).map(([schoolName, schoolRewards]: [string, any]) => (
                  <div key={schoolName} className="mb-6">
                    <h3 className="text-lg font-semibold mb-3">{schoolName}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Account Type</TableHead>
                          <TableHead>Subheader</TableHead>
                          <TableHead>Domain</TableHead>
                          <TableHead>Level 1</TableHead>
                          <TableHead>Level 2</TableHead>
                          <TableHead>Level 3</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {schoolRewards.map((reward: Reward) => (
                          <TableRow key={reward.id}>
                            <TableCell className="font-medium">{reward.account_type}</TableCell>
                            <TableCell className="max-w-[200px] truncate" title={reward.subheader}>
                              {reward.subheader || '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{reward.domain_type}</Badge>
                            </TableCell>
                            <TableCell>{reward.level_1_discount}</TableCell>
                            <TableCell>{reward.level_2_discount || '-'}</TableCell>
                            <TableCell>{reward.level_3_discount || '-'}</TableCell>
                            <TableCell>
                              <Badge 
                                variant={reward.is_active ? "default" : "secondary"}
                                className="cursor-pointer"
                                onClick={() => toggleActiveStatus(reward)}
                              >
                                {reward.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(reward)}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(reward.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  No school-specific rewards created yet. Click "Add Reward" and select a university to create school-specific rewards.
                </div>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

export default AdminRewards
